import * as React from 'react';
import { Drawer as Vaul } from 'vaul';
import { cn } from '../lib/cn.ts';

/**
 * Drawer is the team-card surface on mobile (bottom-sheet). It is Vaul-backed
 * for the drag-to-dismiss gesture. The DESKTOP side panel is {@link SidePanel}
 * (below) — NOT Vaul — because Vaul attaches document-level pointer listeners
 * for its drag gesture that swallow events meant for the interactive globe
 * behind a non-modal panel.
 */
export const Drawer = Vaul.Root;
export const DrawerTrigger = Vaul.Trigger;
export const DrawerClose = Vaul.Close;
export const DrawerPortal = Vaul.Portal;

/** X icon used by both the Vaul drawer and the side panel. */
function CloseIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M18 6 6 18M6 6l12 12" />
    </svg>
  );
}

const CLOSE_BUTTON_CLASS =
  'absolute right-3 top-3 z-10 inline-flex h-8 w-8 items-center justify-center rounded-md text-fg-muted transition-colors duration-150 hover:bg-bg-overlay hover:text-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent';

/**
 * Desktop right-side panel that floats over the globe **without capturing its
 * pointer events**. A plain `position: fixed` `<aside>` occupies only its own
 * 420px box — the globe to its left stays fully draggable/zoomable. Closed
 * state slides it off-screen and disables pointer events. Esc + the corner X
 * close it (no focus trap, by design — the globe must remain usable).
 */
export interface SidePanelProps {
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
  className?: string;
  /** Accessible label for the panel region. */
  label?: string;
}

export function SidePanel({ open, onClose, children, className, label }: SidePanelProps) {
  React.useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  return (
    <aside
      role="dialog"
      aria-modal="false"
      aria-label={label}
      aria-hidden={!open}
      className={cn(
        'fixed inset-y-0 right-0 z-drawer flex w-[min(420px,92vw)] flex-col border-l border-border-default bg-bg-raised text-fg shadow-drawer',
        'transition-transform duration-smooth ease-out-quint will-change-transform',
        open ? 'translate-x-0' : 'pointer-events-none translate-x-full',
        className,
      )}
    >
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        tabIndex={open ? 0 : -1}
        className={CLOSE_BUTTON_CLASS}
      >
        <CloseIcon />
      </button>
      <div className="flex flex-1 flex-col overflow-y-auto">{children}</div>
    </aside>
  );
}

export const DrawerOverlay = React.forwardRef<
  React.ElementRef<typeof Vaul.Overlay>,
  React.ComponentPropsWithoutRef<typeof Vaul.Overlay>
>(({ className, ...props }, ref) => (
  <Vaul.Overlay
    ref={ref}
    className={cn('fixed inset-0 z-drawer bg-bg-sunken/55 backdrop-blur-sm', className)}
    {...props}
  />
));
DrawerOverlay.displayName = 'DrawerOverlay';

export type DrawerContentProps = React.ComponentPropsWithoutRef<typeof Vaul.Content> & {
  side?: 'right' | 'bottom';
  /**
   * Skip the dimming overlay. Used by the atlas drawer (non-modal, `modal={false}`
   * on the root) so the globe stays visible and interactive behind the panel.
   */
  transparentOverlay?: boolean;
};

export const DrawerContent = React.forwardRef<
  React.ElementRef<typeof Vaul.Content>,
  DrawerContentProps
>(({ className, side = 'right', transparentOverlay = false, children, ...props }, ref) => (
  <DrawerPortal>
    {transparentOverlay ? null : <DrawerOverlay />}
    <Vaul.Content
      ref={ref}
      className={cn(
        'fixed z-drawer flex flex-col bg-bg-raised text-fg shadow-drawer',
        side === 'right' &&
          'inset-y-0 right-0 h-dvh w-[min(420px,92vw)] border-l border-border-default',
        side === 'bottom' &&
          'inset-x-0 bottom-0 max-h-[88dvh] rounded-t-2xl border-t border-border-default',
        className,
      )}
      {...props}
    >
      {side === 'bottom' && (
        <div className="mx-auto mt-3 h-1.5 w-12 shrink-0 rounded-pill bg-border-strong" />
      )}
      {/* Always-visible dismiss control (the bottom sheet's drag handle aside).
          Sits above the scroll region so it never scrolls out of reach. */}
      <DrawerClose asChild>
        <button type="button" aria-label="Close" className={CLOSE_BUTTON_CLASS}>
          <CloseIcon />
        </button>
      </DrawerClose>
      <div className="flex flex-1 flex-col overflow-y-auto">{children}</div>
    </Vaul.Content>
  </DrawerPortal>
));
DrawerContent.displayName = 'DrawerContent';

export function DrawerHeader({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        // pr-14 reserves space for the always-present corner close button.
        'flex flex-col gap-1 border-b border-border-subtle py-4 pl-6 pr-14',
        className,
      )}
      {...props}
    />
  );
}

export function DrawerBody({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('flex flex-col gap-4 px-6 py-4', className)} {...props} />;
}

export function DrawerFooter({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn('mt-auto border-t border-border-subtle px-6 py-3', className)}
      {...props}
    />
  );
}

export const DrawerTitle = React.forwardRef<
  React.ElementRef<typeof Vaul.Title>,
  React.ComponentPropsWithoutRef<typeof Vaul.Title>
>(({ className, ...props }, ref) => (
  <Vaul.Title
    ref={ref}
    className={cn('font-display text-lg tracking-tight text-fg', className)}
    {...props}
  />
));
DrawerTitle.displayName = 'DrawerTitle';

export const DrawerDescription = React.forwardRef<
  React.ElementRef<typeof Vaul.Description>,
  React.ComponentPropsWithoutRef<typeof Vaul.Description>
>(({ className, ...props }, ref) => (
  <Vaul.Description
    ref={ref}
    className={cn('text-sm text-fg-muted', className)}
    {...props}
  />
));
DrawerDescription.displayName = 'DrawerDescription';
